import { faKey } from '@fortawesome/pro-regular-svg-icons';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormErrorMessage, SwitchThemeContext, TextField } from '@tryhackme/thm-ui-components';
import { type ChangeEvent, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordInput } from 'src/common/components/password-input/password-input';
import { getPasswordValidationAreaData } from 'src/common/components/password-input/password-input.helpers';
import { GOOGLE_SITE_KEY } from 'src/common/constants';
import { TOTAL_USERS_WITH_MILLION_UNIT } from 'src/common/constants/user';
import { ApiResponseStatus } from 'src/common/enums';
import { Controller, type SubmitHandler, useForm } from 'src/common/form';
import { displayToast } from 'src/common/helpers/display-toast';
import { useSocialLoginUrl } from 'src/common/hooks/use-social-login-url';
import * as yup from 'yup';
import { useSignupMutation } from '../../signup.slice';
import type { SignupResponse } from '../../signup.types';
import { ALREADY_EXISTS_EMAIL_ERROR, DEFAULT_INPUT_VALUES, EMPTY_EMAIL_ERROR, EMPTY_PASSWORD_ERROR, EMPTY_RECAPTCHA_ERROR, GENERIC_ERROR, INVALID_EMAIL_ERROR, INVALID_PASSWORD_ERROR, SignupStep } from './signup-form.constants';
import { StyledContinueButton, StyledContinueWithButtons, StyledForm, StyledFullWidthButton, StyledGoogleLogo, StyledKeyIcon, StyledLine, StyledLinedText, StyledLinedTextRow, StyledLink, StyledLinkedInLogo, StyledRecaptchaContainer, StyledSubtitle, StyledTitle, StyledTsAndCs } from './signup-form.styles';
import { type SignupFormProps, SocialSignupType } from './signup-form.types';

// Needed when using SignupForm inside a Modal component e.g. SignupModal.
// Modal adds pointer-event: 'none' to body. This needs removing else Recaptcha popup is not clickable.
const handleRecaptchaLoaded = () => {
  const body = document.querySelectorAll('body');
  if (body && body[0]) {
    body[0].style.pointerEvents = 'auto';
  }
};
export const SignupForm: React.FC<SignupFormProps> = ({
  initialEmail,
  onOpenChange
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');
  const googleLoginUrl = useSocialLoginUrl({
    redirectTo,
    social: SocialSignupType.GOOGLE
  });
  const linkedInLoginUrl = useSocialLoginUrl({
    redirectTo,
    social: SocialSignupType.LINKEDIN
  });
  const isLinkedInLoginEnabled = useFeatureIsOn('linkedin-social-login');
  const reCaptchaBypass = process.env.REACT_APP_RECAPTCHA_BYPASS === 'true';
  const {
    theme
  } = useContext(SwitchThemeContext);
  const [signup, {
    isLoading: isSignupLoading
  }] = useSignupMutation();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState('');
  const [currentSignUpStep, setCurrentSignUpStep] = useState<SignupStep>(SignupStep.INITIAL);
  const isInitialSignUpStep = currentSignUpStep === SignupStep.INITIAL;
  const isFinalSignUpStep = currentSignUpStep === SignupStep.FINAL;
  const signupFormValidationSchema = yup.object({
    email: yup.string().email(INVALID_EMAIL_ERROR).required(EMPTY_EMAIL_ERROR),
    password: isInitialSignUpStep ? yup.string().notRequired().default('') : yup.string().required(EMPTY_PASSWORD_ERROR).test({
      name: 'invalidPassword',
      message: INVALID_PASSWORD_ERROR,
      test(value) {
        if (value === undefined) {
          return false;
        }
        const passwordValidationData = getPasswordValidationAreaData(value);
        const isValidPassword = passwordValidationData.validationStates.every(state => state.isValid === true);
        return isValidPassword;
      }
    })
  });
  type SignupFromInputs = yup.InferType<typeof signupFormValidationSchema>;
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: {
      errors
    }
  } = useForm<SignupFromInputs>({
    resolver: yupResolver(signupFormValidationSchema),
    defaultValues: {
      ...DEFAULT_INPUT_VALUES,
      email: initialEmail
    }
  });
  const {
    email: emailError,
    password: passwordError
  } = errors;
  const getPasswordValidationStates = (value: string) => {
    const passwordValidationData = getPasswordValidationAreaData(value);
    const isValidPassword = passwordValidationData.validationStates.every(state => state.isValid === true);
    if (isValidPassword) {
      clearErrors('password');
    } else {
      setError('password', {
        type: 'custom',
        message: INVALID_PASSWORD_ERROR
      });
    }
  };
  const proceedToFinalStep = () => setCurrentSignUpStep(SignupStep.FINAL);
  const handleSignup: SubmitHandler<SignupFromInputs> = useCallback(async (data: SignupFromInputs) => {
    const reCaptcha = reCaptchaRef.current?.getValue();
    if (reCaptcha || reCaptchaBypass) {
      setIsLoading(true);
      try {
        const signupSource = searchParams.get('source') ?? '';
        const signupResponse: SignupResponse = await signup({
          reCaptcha,
          ...data,
          signupSource
        }).unwrap();
        reset(DEFAULT_INPUT_VALUES);
        reCaptchaRef.current?.reset();
        clearErrors();
        if (signupResponse.status === ApiResponseStatus.SUCCESS) {
          if (onOpenChange) {
            onOpenChange(false);
          }
          navigate('/onboarding');
        } else {
          displayToast('error', GENERIC_ERROR);
        }
        // biome-ignore lint/suspicious/noExplicitAny: standard error handling
      } catch (error: any) {
        reCaptchaRef.current?.reset();
        if (error?.data?.message === 'Email already exists') {
          setError('email', {
            type: 'custom',
            message: ALREADY_EXISTS_EMAIL_ERROR
          });
        } else {
          displayToast('error', GENERIC_ERROR);
        }
        console.error('Error signing up', error);
      }
      setIsLoading(false);
    } else {
      setRecaptchaError(EMPTY_RECAPTCHA_ERROR);
    }
  }, [clearErrors, navigate, onOpenChange, reCaptchaBypass, reset, searchParams, setError, signup]);
  const onClickSocialSignup = (social: SocialSignupType) => {
    if (onOpenChange) onOpenChange(false);
    const socialLoginUrls = {
      [SocialSignupType.GOOGLE]: googleLoginUrl,
      [SocialSignupType.LINKEDIN]: linkedInLoginUrl
    };
    navigate(socialLoginUrls[social]);
  };
  const onClickSSO = () => {
    if (onOpenChange) {
      onOpenChange(false);
    }
    navigate('/login/sso');
  };
  const renderLinedTextRow = () => <StyledLinedTextRow data-sentry-element="StyledLinedTextRow" data-sentry-component="renderLinedTextRow" data-sentry-source-file="signup-form.tsx">
      <StyledLine data-sentry-element="StyledLine" data-sentry-source-file="signup-form.tsx" />
      <StyledLinedText data-sentry-element="StyledLinedText" data-sentry-source-file="signup-form.tsx">Or</StyledLinedText>
      <StyledLine data-sentry-element="StyledLine" data-sentry-source-file="signup-form.tsx" />
    </StyledLinedTextRow>;
  const renderAdditionalLoginMethods = () => {
    if (currentSignUpStep > SignupStep.INITIAL) {
      return null;
    }
    return <>
        <StyledContinueWithButtons data-sentry-element="StyledContinueWithButtons" data-sentry-source-file="signup-form.tsx">
          <StyledContinueButton type="button" variant="outlined" color="secondary" onClick={() => onClickSocialSignup(SocialSignupType.GOOGLE)} data-sentry-element="StyledContinueButton" data-sentry-source-file="signup-form.tsx">
            <StyledGoogleLogo data-sentry-element="StyledGoogleLogo" data-sentry-source-file="signup-form.tsx" />
            Continue with Google
          </StyledContinueButton>
          {isLinkedInLoginEnabled && <StyledContinueButton type="button" variant="outlined" color="secondary" onClick={() => onClickSocialSignup(SocialSignupType.LINKEDIN)}>
              <StyledLinkedInLogo />
              Continue with LinkedIn
            </StyledContinueButton>}
          <StyledContinueButton type="button" variant="outlined" color="secondary" onClick={onClickSSO} data-sentry-element="StyledContinueButton" data-sentry-source-file="signup-form.tsx">
            <StyledKeyIcon icon={faKey} data-sentry-element="StyledKeyIcon" data-sentry-source-file="signup-form.tsx" />
            Continue with SSO
          </StyledContinueButton>
        </StyledContinueWithButtons>
        {renderLinedTextRow()}
      </>;
  };
  const submitButtonTitle = useMemo(() => currentSignUpStep === SignupStep.INITIAL ? 'Continue' : 'Create account', [currentSignUpStep]);
  const submitForm = handleSubmit(data => {
    if (isFinalSignUpStep) {
      handleSignup(data);
    } else {
      proceedToFinalStep();
    }
  });
  return <>
      <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="signup-form.tsx">Sign Up</StyledTitle>
      <StyledSubtitle data-sentry-element="StyledSubtitle" data-sentry-source-file="signup-form.tsx">Join over {TOTAL_USERS_WITH_MILLION_UNIT} others learning cyber security.</StyledSubtitle>
      <StyledForm
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onSubmit={submitForm} data-sentry-element="StyledForm" data-sentry-source-file="signup-form.tsx">
        {renderAdditionalLoginMethods()}
        <Controller name="email" control={control} render={({
        field: {
          ref,
          ...rest
        }
      }) => <div data-testid="email-container">
              <TextField {...register('email')} id="email" variant="input" label="Email Address" aria-label="email address" placeholder="Example@example.com" isError={!!emailError} errorMessage={emailError ? emailError.message : ''} innerRef={ref} {...rest} />
            </div>} data-sentry-element="Controller" data-sentry-source-file="signup-form.tsx" />

        {currentSignUpStep === SignupStep.FINAL && <>
            <Controller name="password" control={control} render={({
          field: {
            ref,
            ...rest
          }
        }) => <div data-testid="password-container">
                  <PasswordInput {...register('password', {
            /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
            onChange: (e: ChangeEvent<HTMLInputElement>) => getPasswordValidationStates(e.target.value)
          })} id="password" label="Password" placeholder="Password" aria-label="password" showProgressAndValidation isError={!!passwordError} errorMessage={passwordError ? passwordError.message : ''} innerRef={ref} {...rest} />
                </div>} />
            <StyledRecaptchaContainer>
              <ReCAPTCHA sitekey={GOOGLE_SITE_KEY} ref={reCaptchaRef} theme={theme === 'dark' ? 'dark' : 'light'} data-testid="recaptcha-comp" asyncScriptOnLoad={onOpenChange ? handleRecaptchaLoaded : undefined} />
              {recaptchaError && <FormErrorMessage>{EMPTY_RECAPTCHA_ERROR}</FormErrorMessage>}
            </StyledRecaptchaContainer>
          </>}

        <StyledFullWidthButton color="primary" type="submit" isLoading={isLoading || isSignupLoading} data-testid="signup-button" data-sentry-element="StyledFullWidthButton" data-sentry-source-file="signup-form.tsx">
          {submitButtonTitle}
        </StyledFullWidthButton>
      </StyledForm>
      <StyledTsAndCs data-sentry-element="StyledTsAndCs" data-sentry-source-file="signup-form.tsx">
        By signing up, you are agreeing to our{' '}
        <StyledLink onClick={onOpenChange ? () => onOpenChange(false) : undefined} to="/legal/terms-of-use" data-sentry-element="StyledLink" data-sentry-source-file="signup-form.tsx">
          Terms and Conditions
        </StyledLink>
        . Already have an account?{' '}
        <StyledLink onClick={onOpenChange ? () => onOpenChange(false) : undefined} to="/login" data-sentry-element="StyledLink" data-sentry-source-file="signup-form.tsx">
          Log in
        </StyledLink>
        .
      </StyledTsAndCs>
    </>;
};